// service/discount.ts
import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

const DISCOUNT_ENDPOINT = {
  GET_DISCOUNT: "/discount",
  TRANSPORT_REMAINING_MONTHS: "/discount/transport-remaining-months",
}

export type DiscountData = {
  discount: number
  discountType: string
}

export type FeeType = {
  _id: string
  name: string
  amount: number
  feeGroup: string
  feeGroupName: string // Added this new field
  applicableFrom?: string
  description?: string
}

export type DiscountResponse = {
  success?: boolean
  message: string
  data: {
    discount: DiscountData | null
    remainingMonths: string[]
    // monthKey ("YYYY-MM") -> FeeType _ids already invoiced for that month.
    // A month still shows in remainingMonths if ANY fee type in the group
    // (e.g. STEAM) is still outstanding for it, even if others (e.g.
    // Tuition) are already paid — this says which ones those are.
    paidFeeTypesByMonth?: Record<string, string[]>
    otherFees: FeeType[] // Changed from examinationFees to otherFees
  }
}

export type UpdateDiscountRequest = {
  discount: string
  discountType?: string
}

export type UpdateDiscountResponse = {
  success: boolean
  message: string
  data: DiscountData
}

const getDiscountByEnrollment = async (
  enrollmentId: string
): Promise<DiscountResponse> => {
  const params = {
    enrollmentId,
  }

  const response = await apiClient({
    url: appendQueryParams(DISCOUNT_ENDPOINT.GET_DISCOUNT, params),
    method: "GET",
  })
  return response.data
}

const updateDiscount = async (
  enrollmentId: string,
  discountData: UpdateDiscountRequest
): Promise<UpdateDiscountResponse> => {
  const params = {
    enrollmentId,
  }

  const response = await apiClient({
    url: appendQueryParams(DISCOUNT_ENDPOINT.GET_DISCOUNT, params),
    method: "PUT",
    data: discountData,
  })
  return response.data
}

export type TransportRemainingMonth = {
  month: string
  amount: number
  allocationId: string
}

export type TransportRemainingMonthsResponse = {
  success: boolean
  data: {
    studentId?: string
    sessionId?: string
    transportFeeAmount?: number
    transportFeeStatus?: string
    remainingMonths: TransportRemainingMonth[]
    calculationBasis?: string
    message?: string
    student?: {
      _id?: string
      grNumber?: string
      name?: string
      userId?: {
        _id?: string
        firstName?: string
        lastName?: string
      }
      class?: { _id?: string; name?: string }
      section?: { _id?: string; name?: string }
    }
    currentEnrollment?: {
      _id?: string
      class?: { _id?: string; name?: string }
      section?: { _id?: string; name?: string }
    }
  }
}

const getTransportRemainingMonths = async (
  studentId: string,
  sessionId: string
): Promise<TransportRemainingMonthsResponse> => {
  const response = await apiClient({
    url: appendQueryParams(DISCOUNT_ENDPOINT.TRANSPORT_REMAINING_MONTHS, {
      studentId,
      sessionId,
    }),
    method: "GET",
  })
  return response.data
}

export { getDiscountByEnrollment, getTransportRemainingMonths, updateDiscount }